import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import logo from '../../assets/logo.png';
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Users,
  UserCheck,
  BarChart3,
  Settings,
  FolderTree,
  Plus,
  UserCog,
  ChevronDown,
  ChevronRight,
  List,
  Eye,
  Edit,
  Trash2,
  TrendingUp
} from 'lucide-react';

const Sidebar = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState({});

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const adminMenuItems = [
    {
      title: 'Dashboard',
      icon: LayoutDashboard,
      path: '/dashboard',
    },
    {
      title: 'Seller Management',
      icon: Users,
      path: '/admin/sellers',
      children: [
        {
          title: 'All Sellers',
          path: '/admin/sellers',
          icon: List,
        },
        {
          title: 'Pending Approval',
          path: '/admin/sellers/pending',
          icon: UserCheck,
        },
      ],
    },
    {
      title: 'Product Management',
      icon: Package,
      path: '/admin/products',
      children: [
        {
          title: 'All Products',
          path: '/admin/products',
          icon: List,
        },
        {
          title: 'Pending Approval',
          path: '/admin/products/pending',
          icon: UserCheck,
        },
      ],
    },
    {
      title: 'Categories',
      icon: FolderTree,
      path: '/admin/categories',
      children: [
        {
          title: 'View Categories',
          path: '/admin/categories',
          icon: Eye,
        },
        {
          title: 'Add Category',
          path: '/admin/categories/add',
          icon: Plus,
        },
      ],
    },
    {
      title: 'Order Management',
      icon: ShoppingCart,
      path: '/admin/orders',
    },
    {
      title: 'Analytics',
      icon: BarChart3,
      path: '/admin/analytics',
      children: [
        {
          title: 'Overview',
          path: '/admin/analytics',
          icon: TrendingUp,
        },
        {
          title: 'Revenue',
          path: '/admin/analytics/revenue',
          icon: BarChart3,
        },
        {
          title: 'Sellers',
          path: '/admin/analytics/sellers',
          icon: Users,
        },
      ],
    },
  ];

  const sellerMenuItems = [
    {
      title: 'Dashboard',
      icon: LayoutDashboard,
      path: '/dashboard',
    },
    {
      title: 'Products',
      icon: Package,
      path: '/seller/products',
      children: [
        {
          title: 'All Products',
          path: '/seller/products',
          icon: List,
        },
        {
          title: 'Add Product',
          path: '/seller/products/add',
          icon: Plus,
        },
        {
          title: 'Low Stock',
          path: '/seller/products/low-stock',
          icon: Package,
        },
      ],
    },
    {
      title: 'Orders',
      icon: ShoppingCart,
      path: '/seller/orders',
      children: [
        {
          title: 'All Orders',
          path: '/seller/orders',
          icon: List,
        },
        {
          title: 'Pending',
          path: '/seller/orders/pending',
          icon: UserCheck,
        },
        {
          title: 'Completed',
          path: '/seller/orders/completed',
          icon: UserCheck,
        },
      ],
    },
    {
      title: 'Analytics',
      icon: BarChart3,
      path: '/seller/analytics',
    },
    {
      title: 'Profile',
      icon: UserCog,
      path: '/seller/profile',
    },
  ];

  const menuItems = user?.role === 'admin' ? adminMenuItems : sellerMenuItems;

  const toggleExpanded = (itemTitle) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemTitle]: !prev[itemTitle]
    }));
  };

  const MenuItem = ({ item, isChild = false }) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems[item.title];
    const isItemActive = isActive(item.path) || (hasChildren && item.children.some(child => isActive(child.path)));
    const Icon = item.icon;
    const active = isActive(item.path);

    if (hasChildren) {
      return (
        <li className={`mb-1 ${isChild ? 'ml-4' : ''}`}>
          <button
            onClick={() => toggleExpanded(item.title)}
            className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-200 ${
              isItemActive
                ? 'bg-blue-800 text-blue-100 border-r-2 border-blue-400'
                : 'text-blue-200 hover:bg-blue-800 hover:text-white'
            }`}
          >
            <div className="flex items-center">
              {item.icon && (
                <item.icon className="h-5 w-5 mr-3 flex-shrink-0" />
              )}
              <span className="truncate">{item.title}</span>
            </div>
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 flex-shrink-0" />
            ) : (
              <ChevronRight className="h-4 w-4 flex-shrink-0" />
            )}
          </button>
          {isExpanded && (
            <ul className="mt-1 space-y-1">
              {item.children.map((child, index) => (
                <MenuItem key={index} item={child} isChild={true} />
              ))}
            </ul>
          )}
        </li>
      );
    }

    return (
      <li className={`mb-1 ${isChild ? 'ml-4' : ''}`}>
        <Link
          to={item.path}
          className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-200 ${
            isActive(item.path)
              ? 'bg-blue-800 text-blue-100 border-r-2 border-blue-400'
              : 'text-blue-200 hover:bg-blue-800 hover:text-white'
          } ${isChild ? 'text-xs py-2 pl-8' : ''}`}
        >
          {item.icon && (
            <item.icon className={`${isChild ? 'h-4 w-4' : 'h-5 w-5'} mr-3 flex-shrink-0`} />
          )}
          <span className="truncate">{item.title}</span>
        </Link>
      </li>
    );
  };

  return (
    <aside className="fixed left-0 top-0 h-full w-64 bg-blue-900 border-r border-blue-800 overflow-y-auto z-40">
      <div className="p-4 pt-4">
        {/* Logo Section */}
        <div className="flex items-center justify-center mb-4 border-b border-blue-800">
          <Link to="/dashboard" className="flex flex-col items-center space-y-2">
            <img 
              src={logo} 
              alt="Buildify" 
              className="h-16 w-full object-contain"
            />
          </Link>
        </div>
        
        <nav>
          <ul className="space-y-2 py-4 list-none">
            {menuItems.map((item, index) => (
              <MenuItem key={index} item={item} />
            ))}
          </ul>
        </nav>

        {/* Seller Status */}
        {user?.role === 'seller' && (
          <div className="mt-8 p-4 bg-blue-800 rounded-lg border border-blue-700">
            <h3 className="text-sm font-medium text-blue-100 mb-2">Account Status</h3>
            <div className="flex items-center">
              <div
                className={`w-2 h-2 rounded-full mr-2 ${
                  user.isApproved ? 'bg-green-500' : 'bg-yellow-500'
                }`}
              />
              <span className="text-sm text-blue-200">
                {user.isApproved ? 'Approved' : 'Pending Approval'}
              </span>
            </div>
            {!user.isApproved && (
              <p className="text-xs text-blue-300 mt-1">
                Your account is under review. You'll be notified once approved.
              </p>
            )}
          </div>
        )}

      </div>
    </aside>
  );
};

export default Sidebar;
